import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { useState } from "react"
import { useNavigate } from "react-router"
import { usePatchProduct, useProductDetail } from "../../../api/productsApi"
import type { ProductOut, ProductUpdateIn } from "../../../api/types/masterData"
import {
  PRODUCT_STATUS_VALUES,
  productStatusLabel,
} from "../../../constants/masterDataFilters"
import { AdditionalAttributes } from "../../shared/AdditionalAttributes"
import { CollapsibleSection } from "../../shared/CollapsibleSection"
import { EditActionBar } from "../../shared/EditActionBar"
import { FieldsGrid } from "../../shared/FieldsGrid"
import { FieldsSectionSkeleton } from "../../shared/FieldsSectionSkeleton"
import { McPill } from "../../shared/McPill"
import { EntityFieldsHeader } from "../EntityFieldsHeader"
import {
  useEntityFieldsState,
  type EntityFieldsState,
} from "../useEntityFieldsState"
import { useScrollIntoViewOnEdit } from "../useScrollIntoViewOnEdit"

function productStatusTone(
  status: string,
): "success" | "attention" | "default" {
  if (status === "active") return "success"
  if (status === "review") return "attention"
  return "default"
}

interface ProductFieldsState
  extends Omit<EntityFieldsState<ProductOut, ProductUpdateIn>, "entity"> {
  product: ProductOut | undefined
}

export function useProductFieldsState(): ProductFieldsState {
  const { entity: product, ...rest } = useEntityFieldsState(
    useProductDetail,
    usePatchProduct,
  )
  return { product, ...rest }
}

interface SectionProps {
  state: ProductFieldsState
}

export function ProductFieldsHeader({ state }: SectionProps) {
  return (
    <EntityFieldsHeader
      entity={state.product}
      isLoading={state.isLoading}
      editing={state.editing}
      setEditing={state.setEditing}
      labelKey="MC_MD_TAB_PRODUCTS"
      name={state.product?.name}
      subtitle={state.product?.productNumber}
    />
  )
}

export function ProductFieldsBody({ state }: SectionProps) {
  const { translate } = useTranslation()
  const navigate = useNavigate()
  const {
    product,
    isLoading,
    editing,
    edits,
    setEdits,
    isPending,
    handleSave,
    handleCancel,
  } = state
  const [fieldsOpen, setFieldsOpen] = useState(false)
  const fieldsRef = useScrollIntoViewOnEdit<HTMLDivElement>(editing)

  if (isLoading) return <FieldsSectionSkeleton count={4} />
  if (!product) return null

  return (
    <div ref={fieldsRef} className="flex flex-col">
      <CollapsibleSection
        label={translate("MC_MD_FIELDS_SECTION")}
        open={editing || fieldsOpen}
        onOpenChange={setFieldsOpen}
      >
        <FieldsGrid
          fields={[
            {
              label: translate("MC_MD_FIELD_PRODUCT_NUMBER"),
              value: <span className="font-mono">{product.productNumber}</span>,
            },
            {
              label: translate("MC_MD_FIELD_NAME"),
              value: editing ? (
                <Input
                  value={edits.name ?? product.name}
                  onChange={(e) =>
                    setEdits((prev) => ({ ...prev, name: e.target.value }))
                  }
                  className="h-7 w-full border-grey4 bg-grey2 text-xs text-white shadow-none focus-visible:border-yellow1 focus-visible:ring-0 focus-visible:ring-offset-0"
                />
              ) : (
                product.name
              ),
            },
            {
              label: translate("MC_MD_PANEL_COL_STATUS"),
              value: editing ? (
                <Select
                  value={edits.status ?? product.status}
                  onValueChange={(value) =>
                    setEdits((prev) => ({ ...prev, status: value }))
                  }
                >
                  <SelectTrigger className="h-7 w-full border-grey4 bg-grey2 text-xs text-white shadow-none focus:ring-0 focus:ring-offset-0 focus-visible:border-yellow1 focus-visible:ring-0 focus-visible:ring-offset-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {/* A legacy/import status outside the canonical set stays
                        selectable, so editing never blanks the current value. */}
                    {(PRODUCT_STATUS_VALUES.includes(
                      (edits.status ?? product.status) as never,
                    )
                      ? PRODUCT_STATUS_VALUES
                      : [
                          ...PRODUCT_STATUS_VALUES,
                          edits.status ?? product.status,
                        ]
                    ).map((value) => (
                      <SelectItem key={value} value={value}>
                        {productStatusLabel(value, translate)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <McPill tone={productStatusTone(product.status)}>
                  {productStatusLabel(product.status, translate)}
                </McPill>
              ),
            },
            {
              label: translate("MC_MD_FIELD_REVISION"),
              value: editing ? (
                <Input
                  value={edits.revision ?? product.revision ?? ""}
                  onChange={(e) =>
                    setEdits((prev) => ({ ...prev, revision: e.target.value }))
                  }
                  className="h-7 w-full border-grey4 bg-grey2 text-xs text-white shadow-none focus-visible:border-yellow1 focus-visible:ring-0 focus-visible:ring-offset-0"
                />
              ) : product.revision ? (
                <span className="font-mono">{product.revision}</span>
              ) : (
                "—"
              ),
            },
            {
              label: translate("MC_MD_PRODUCT_OWN_SCIP"),
              title: translate("MC_MD_PRODUCT_OWN_SCIP_HINT"),
              value: editing ? (
                <Input
                  value={edits.scipNumber ?? product.scipNumber ?? ""}
                  onChange={(e) =>
                    setEdits((prev) => ({
                      ...prev,
                      scipNumber: e.target.value,
                    }))
                  }
                  className="font-mono h-7 w-full border-grey4 bg-grey2 text-xs text-white shadow-none focus-visible:border-yellow1 focus-visible:ring-0 focus-visible:ring-offset-0"
                />
              ) : product.scipNumber ? (
                <span className="font-mono break-all">
                  {product.scipNumber}
                </span>
              ) : (
                "—"
              ),
            },
          ]}
        />
        {product.soldAsIs && (
          <p className="mt-2 text-[12px] text-grey5">
            {translate("MC_MD_PRODUCT_SOLD_AS_IS")}
            {product.sourceArticleId && (
              <button
                type="button"
                onClick={() =>
                  navigate(`../articles?id=${product.sourceArticleId}`)
                }
                className="ml-1.5 border-0 bg-transparent p-0 text-yellow1 underline underline-offset-2"
              >
                {translate("MC_MD_PRODUCT_SOURCE_ARTICLE_LINK")}
              </button>
            )}
          </p>
        )}
      </CollapsibleSection>

      {product.additionalAttributes && (
        <AdditionalAttributes attributes={product.additionalAttributes} />
      )}

      {editing && (
        <EditActionBar
          onCancel={handleCancel}
          onSave={handleSave}
          isPending={isPending}
        />
      )}
    </div>
  )
}
